import { useQuery } from '@tanstack/react-query'
import { useMangoApiClient } from '~/atoms/MangoApi'
import { useUserState } from '~/atoms/UserState'
import { useReportCcuaGatewayQueryError } from '~/queries/ccuaGatewaySentry'

import { ApiError } from '@mangovoice/types'

export interface EventLogAgentUser {
  id: string
  full_name?: string
}

const displayName = (user: {
  full_name?: string
  name?: string
  first_name?: string
  last_name?: string
  id?: number
}): string =>
  user.full_name ||
  user.name ||
  [user.first_name, user.last_name].filter(Boolean).join(' ').trim() ||
  (user.id != null ? String(user.id) : '')

export const useEventLogAgentUsers = (): EventLogAgentUser[] => {
  const api = useMangoApiClient()
  const { pbx } = useUserState()

  const query = useQuery<EventLogAgentUser[], ApiError>({
    queryKey: ['eventLogAgentUsers', pbx?.id],
    queryFn: async () => {
      if (!pbx?.id) return []
      const { ok, data } = await api.users.getPbxUsers({
        pbx_id: pbx.id,
        limit: 999,
        paginate: false,
      })
      if (!ok || !data) {
        return Promise.reject('Failed to fetch PBX users')
      }
      const rows = Array.isArray(data) ? data : (data.results ?? [])
      const users: EventLogAgentUser[] = []
      for (const user of rows) {
        const uuid = (user as { uuid?: string }).uuid
        if (!uuid) continue
        users.push({
          id: uuid,
          full_name: displayName(
            user as {
              name?: string
              first_name?: string
              last_name?: string
              id?: number
            },
          ),
        })
      }
      return users
    },
    enabled: !!pbx?.id,
  })

  useReportCcuaGatewayQueryError({
    enabled: true,
    pbxId: pbx?.id,
    source: 'event-log-agent-users',
    queryKey: ['eventLogAgentUsers', pbx?.id],
    error: query.error,
    isError: query.isError,
    isSuccess: query.isSuccess,
  })

  return query.data ?? []
}
